/**
 * useBackups — Monthly automatic snapshot of TIME Watch data.
 *
 * Triggers `POST /api/backups/ensure` for the current period (YYYY-MM) once
 * per session, when the user is authenticated. The backend is idempotent:
 * repeated calls with the same period return the existing snapshot untouched.
 *
 * Lists past backups so the Area Personale can render the 'Archivio del
 * Tempo' section.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function currentPeriod() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}`;
}

export function useBackups(snapshotBuilder) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setBackups([]);
      setLoaded(true);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/backups`);
      setBackups(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      if (!isAuthenticated) {
        setBackups([]);
        setLoaded(true);
        return;
      }
      try {
        const period = currentPeriod();
        const snapshot =
          typeof snapshotBuilder === "function" ? snapshotBuilder() : {};
        if (snapshot && Object.keys(snapshot).length > 0) {
          await axios.post(`${API}/backups/ensure`, { period, snapshot });
        }
      } catch {
        /* silent — backup is best-effort */
      }
      if (!cancelled) await refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, refresh, snapshotBuilder]);

  // Ricarica la lista dopo un rollover mensile (nuovo backup possibile).
  useEffect(() => {
    const h = () => { refresh(); };
    window.addEventListener("ti:rollover-done", h);
    return () => window.removeEventListener("ti:rollover-done", h);
  }, [refresh]);

  return { backups, loaded, error, refresh };
}
